import React, { useEffect, useRef } from 'react';
import { useReducedMotion } from 'framer-motion';

type Wave = {
  amp: number;
  len: number;
  speed: number;
  phase: number;
  y: number;
  width: number;
  alpha: number;
};

type Pulse = {
  waveIndex: number;
  t: number; // 0..1 along x
  speed: number;
  life: number;
  size: number;
};

const ACCENT = { r: 231, g: 111, b: 42 };

const HeroNetwork: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = 0;
    let height = 0;
    let dpr = 1;
    let raf = 0;
    let running = true;
    let time = 0;
    let waves: Wave[] = [];
    let pulses: Pulse[] = [];
    let spawnTimer = 0;
    let ringPulses: { x: number; y: number; r: number; alpha: number }[] = [];

    const buildWaves = () => {
      waves = [
        { amp: 28, len: 0.0045, speed: 0.35, phase: 0.2, y: 0.22, width: 1.1, alpha: 0.22 },
        { amp: 42, len: 0.0032, speed: 0.22, phase: 1.4, y: 0.38, width: 1.4, alpha: 0.18 },
        { amp: 36, len: 0.0055, speed: -0.28, phase: 2.1, y: 0.52, width: 0.9, alpha: 0.28 },
        { amp: 55, len: 0.0024, speed: 0.18, phase: 0.8, y: 0.64, width: 1.6, alpha: 0.14 },
        { amp: 24, len: 0.0068, speed: -0.4, phase: 3.2, y: 0.76, width: 0.8, alpha: 0.32 },
        { amp: 48, len: 0.0028, speed: 0.15, phase: 4.0, y: 0.88, width: 1.2, alpha: 0.12 },
        { amp: 18, len: 0.0075, speed: 0.5, phase: 1.1, y: 0.30, width: 0.6, alpha: 0.16 },
        { amp: 20, len: 0.006, speed: -0.32, phase: 2.7, y: 0.58, width: 0.55, alpha: 0.2 },
        { amp: 14, len: 0.009, speed: 0.45, phase: 0.4, y: 0.71, width: 0.5, alpha: 0.18 },
      ];
    };

    const waveY = (wave: Wave, x: number, t: number) => {
      const baseY = height * wave.y;
      const amp = wave.amp * (height / 800);
      const phase = wave.phase + t * wave.speed;
      return (
        baseY +
        Math.sin(x * wave.len + phase) * amp +
        Math.sin(x * wave.len * 0.45 + phase * 1.3) * amp * 0.35
      );
    };

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      width = parent.clientWidth;
      height = parent.clientHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildWaves();
      pulses = [];
      ringPulses = [];
      // seed so pulses/rings are visible immediately
      for (let i = 0; i < 4; i++) {
        pulses.push({
          waveIndex: i % waves.length,
          t: 0.15 + i * 0.2,
          speed: (i % 2 === 0 ? 1 : -1) * (0.0035 + Math.random() * 0.002),
          life: 1,
          size: 3,
        });
      }
      spawnTimer = 0;
    };

    const spawnTravelingPulse = () => {
      if (pulses.length > 12) return;
      pulses.push({
        waveIndex: Math.floor(Math.random() * waves.length),
        t: Math.random() > 0.5 ? 0 : 1,
        speed: (Math.random() > 0.5 ? 1 : -1) * (0.003 + Math.random() * 0.004),
        life: 1,
        size: 2.5 + Math.random() * 2.5,
      });
    };

    const spawnRing = () => {
      if (ringPulses.length > 5) return;
      const wave = waves[Math.floor(Math.random() * Math.min(6, waves.length))];
      const x = width * (0.15 + Math.random() * 0.7);
      ringPulses.push({
        x,
        y: waveY(wave, x, time),
        r: 4,
        alpha: 0.55,
      });
    };

    const drawWave = (wave: Wave, t: number) => {
      // main stroke
      ctx.beginPath();
      for (let x = 0; x <= width; x += 3) {
        const y = waveY(wave, x, t);
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${wave.alpha})`;
      ctx.lineWidth = wave.width;
      ctx.lineCap = 'round';
      ctx.stroke();

      // soft glow
      ctx.beginPath();
      for (let x = 0; x <= width; x += 6) {
        const y = waveY(wave, x, t);
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${wave.alpha * 0.35})`;
      ctx.lineWidth = wave.width * 4;
      ctx.stroke();
    };

    const draw = () => {
      if (!running) return;
      if (!prefersReducedMotion) time += 0.016;

      ctx.clearRect(0, 0, width, height);

      for (const wave of waves) {
        drawWave(wave, time);
      }

      if (!prefersReducedMotion) {
        spawnTimer += 1;
        if (spawnTimer % 22 === 0) spawnTravelingPulse();
        if (spawnTimer % 55 === 0) spawnRing();

        // traveling pulses along waves
        pulses = pulses.filter((p) => p.t >= -0.02 && p.t <= 1.02 && p.life > 0.05);
        for (const p of pulses) {
          p.t += p.speed;
          p.life *= 0.998;
          const wave = waves[p.waveIndex];
          if (!wave) continue;
          const x = p.t * width;
          const y = waveY(wave, x, time);

          // core
          ctx.beginPath();
          ctx.arc(x, y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${0.95 * p.life})`;
          ctx.fill();

          // halo
          ctx.beginPath();
          ctx.arc(x, y, p.size * 4, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${0.18 * p.life})`;
          ctx.fill();

          // short bright segment on the wave
          ctx.beginPath();
          ctx.moveTo(x - 18, waveY(wave, x - 18, time));
          ctx.lineTo(x + 18, waveY(wave, x + 18, time));
          ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${0.7 * p.life})`;
          ctx.lineWidth = wave.width + 1.6;
          ctx.lineCap = 'round';
          ctx.stroke();
        }

        // expanding ring pulses
        ringPulses = ringPulses.filter((r) => r.alpha > 0.02 && r.r < Math.max(width, height) * 0.35);
        for (const r of ringPulses) {
          r.r += 1.8;
          r.alpha *= 0.97;
          ctx.beginPath();
          ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${r.alpha})`;
          ctx.lineWidth = 1.4;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(r.x, r.y, r.r * 0.6, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${ACCENT.r},${ACCENT.g},${ACCENT.b},${r.alpha * 0.55})`;
          ctx.lineWidth = 0.9;
          ctx.stroke();
        }
      }

      raf = requestAnimationFrame(draw);
    };

    resize();
    draw();

    const onResize = () => resize();
    window.addEventListener('resize', onResize);

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else {
        running = true;
        raf = requestAnimationFrame(draw);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [prefersReducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none"
      aria-hidden="true"
    />
  );
};

export default HeroNetwork;
